import { Empty, Space, Tag, theme, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Check } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { OrganizationRoleFull, PermissionsLocalized } from 'src/api/main'
import { ProTableContainer } from 'src/components'

import {
  collectCustomCodesFromRoles,
  roleHasCustomPermissionCode
} from '../utils/workspaceRoleCustomPermissions'

const { Text } = Typography

type MatrixRowApi = {
  key: string
  kind: 'api'
  permission: PermissionsLocalized
  label: string
}

type MatrixRowCustom = {
  key: string
  kind: 'custom'
  code: string
  label: string
}

type MatrixRow = MatrixRowApi | MatrixRowCustom

const permissionRowLabel = (p: PermissionsLocalized): string => {
  const raw = (p as PermissionsLocalized & { label?: unknown }).label
  if (raw && typeof raw === 'object' && raw !== null) {
    const en = (raw as Record<string, unknown>).en
    if (typeof en === 'string' && en.length > 0) {
      return en
    }
  }
  return p.code
}

const roleHasPermission = (
  role: OrganizationRoleFull,
  permissionId: number
): boolean =>
  role.is_admin || role.permissions.some((p) => p.id === permissionId)

type Props = {
  roles: OrganizationRoleFull[]
  permissions: PermissionsLocalized[]
  loading?: boolean
}

export const WorkspaceRolePermissionsMatrix = ({
  roles,
  permissions,
  loading = false
}: Props) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  const orgPermissions = useMemo(
    () => permissions.filter((p) => p.application_repository_id == null),
    [permissions]
  )

  const customCodes = useMemo(() => collectCustomCodesFromRoles(roles), [roles])

  const dataSource: MatrixRow[] = useMemo(() => {
    const apiRows: MatrixRowApi[] = orgPermissions.map((p) => ({
      key: `api:${p.id}`,
      kind: 'api',
      permission: p,
      label: permissionRowLabel(p)
    }))
    const customRows: MatrixRowCustom[] = customCodes.map((code) => ({
      key: `custom:${code}`,
      kind: 'custom',
      code,
      label: code
    }))

    return [...apiRows, ...customRows]
  }, [orgPermissions, customCodes])

  const columns: ColumnsType<MatrixRow> = useMemo(() => {
    const base: ColumnsType<MatrixRow> = [
      {
        title: t('workspace.roles.matrix.permission'),
        dataIndex: 'label',
        key: 'permission',
        fixed: 'left',
        width: 280,
        ellipsis: true,
        render: (_: string, row) =>
          row.kind === 'api' ? (
            <Space orientation="vertical" size={0}>
              <Text>{row.label}</Text>
              <Text type="secondary" style={{ fontSize: 12 }} code>
                {row.permission.code}
              </Text>
            </Space>
          ) : (
            <Space orientation="vertical" size={0}>
              <Space size={6} wrap>
                <Text code>{row.code}</Text>
                <Tag style={{ margin: 0 }}>
                  {t('workspace.roles.matrix.customCodeBadge')}
                </Tag>
              </Space>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('workspace.roles.matrix.customCodeHint')}
              </Text>
            </Space>
          )
      }
    ]

    const roleCols: ColumnsType<MatrixRow> = roles.map((role) => ({
      title: (
        <Tooltip
          title={
            role.is_admin ? t('workspace.roles.matrix.adminTooltip') : undefined
          }
        >
          <Space
            orientation="vertical"
            size={0}
            style={{ textAlign: 'center' }}
          >
            <Text strong ellipsis style={{ maxWidth: 120 }}>
              {role.name}
            </Text>
            {role.is_admin ? (
              <Tag color="blue" style={{ margin: 0 }}>
                {t('workspace.roles.adminBadge')}
              </Tag>
            ) : null}
          </Space>
        </Tooltip>
      ),
      key: `role-${role.id}`,
      align: 'center',
      width: 112,
      render: (_: unknown, row) => {
        const granted =
          row.kind === 'api'
            ? roleHasPermission(role, row.permission.id)
            : role.is_admin || roleHasCustomPermissionCode(role, row.code)

        return granted ? (
          <Check size={16} color={token.colorSuccess} />
        ) : (
          <Text type="secondary">{t('ui.noValue')}</Text>
        )
      }
    }))

    return [...base, ...roleCols]
  }, [roles, t, token])

  const hasRows = dataSource.length > 0

  if (!loading && roles.length === 0) {
    return <Empty description={t('workspace.roles.matrix.noRoles')} />
  }

  if (!loading && !hasRows) {
    return <Empty description={t('workspace.roles.matrix.noPermissions')} />
  }

  return (
    <ProTableContainer<MatrixRow>
      options={false}
      showSearch={false}
      rowKey="key"
      loading={loading}
      pagination={false}
      columns={columns}
      dataSource={dataSource}
      locale={{
        emptyText: (
          <Empty description={t('workspace.roles.matrix.noPermissions')} />
        )
      }}
    />
  )
}
